/*************************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 *  NOTICE:  All information contained herein is, and remains
 *  the property of Adobe and its suppliers, if any. The intellectual
 *  and technical concepts contained herein are proprietary to Adobe
 *  and its suppliers and are protected by all applicable intellectual
 *  property laws, including trade secret and copyright laws.
 *  Dissemination of this information or reproduction of this material
 *  is strictly forbidden unless prior written permission is obtained
 *  from Adobe.
 **************************************************************************/
import { EventEmitter } from './events.js';
import { ComponentLoader, MODULE_CONFIGS, } from '@ccx-public/component-loader-mf';
import SharedContext from './SharedContext.js';
let _rootSDK;
export class AUPSDK extends ComponentLoader {
    constructor(_namedRelease, initialConfigOptions) {
        // Only set up fallbacks if we're in a Node.js environment (no window and no document)
        // and offlineFallbacks is not explicitly set to false
        if (typeof window === 'undefined' &&
            typeof document === 'undefined' &&
            typeof require !== 'undefined' &&
            ((initialConfigOptions === null || initialConfigOptions === void 0 ? void 0 : initialConfigOptions.offlineFallbacks) === undefined ||
                initialConfigOptions.offlineFallbacks === true)) {
            try {
                // Dynamic require for Node.js environments
                // eslint-disable-next-line @typescript-eslint/no-var-requires, @typescript-eslint/no-unsafe-assignment
                const offlineFallbacks = 
                // eslint-disable-next-line @typescript-eslint/no-var-requires
                require(`@ccx-public/collaboration-sdk-fallbacks-${_namedRelease}`);
                initialConfigOptions = Object.assign(Object.assign({}, initialConfigOptions), { offlineFallbacks });
            }
            catch (e) {
                throw new Error(`Failed to load collaboration-sdk-fallbacks-${_namedRelease}. Please ensure the package is installed in your package.json.`);
            }
        }
        super({
            config: initialConfigOptions,
            cdnEnvironment: initialConfigOptions === null || initialConfigOptions === void 0 ? void 0 : initialConfigOptions.cdnEnvironment,
        });
        this._namedRelease = _namedRelease;
        this._events = new EventEmitter();
        this._sharedContextMap = new Map();
    }
    async _load(name) {
        return super._load(name, this._namedRelease, (module) => module.init(this));
    }
    emit(event, ...args) {
        return this._events.emit(event, ...args);
    }
    on(event, listener) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this._events.on(event, listener);
        return this;
    }
    off(event, listener) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this._events.off(event, listener);
        return this;
    }
    getConfig() {
        return Object.freeze(super.getConfig());
    }
    async updateConfig(options) {
        await super.updateConfig(options);
        this.emit('change', this);
    }
    async showDialog(tagName, attributes = {}) {
        if (typeof self.document === 'undefined') {
            throw new Error('AUPSDK.showDialog() is only available in the browser');
        }
        const { showDialog } = this.getConfig();
        if (!showDialog) {
            throw new Error('Config.showDialog is not defined');
        }
        await this.loadUIComponent(tagName);
        const element = document.createElement(tagName);
        if (attributes) {
            for (const [key, value] of Object.entries(attributes)) {
                if (value !== null && value !== undefined) {
                    element.setAttribute(key, value.toString());
                }
            }
        }
        let resolveDataPromise;
        let resolveCloseTypePromise;
        const dataPromise = new Promise((resolve) => {
            resolveDataPromise = resolve;
        });
        const closeTypePromise = new Promise((resolve) => {
            resolveCloseTypePromise = resolve;
        });
        const onClose = (options) => {
            const closeType = (options === null || options === void 0 ? void 0 : options.type) || 'close';
            resolveCloseTypePromise(closeType);
            const jsonData = 'returnValue' in element
                ? typeof element.returnValue === 'string'
                    ? JSON.parse(element.returnValue || '{}')
                    : element.returnValue
                : undefined;
            resolveDataPromise(jsonData);
        };
        await showDialog(element, attributes, onClose);
        return {
            async data() {
                return dataPromise;
            },
            async closeType() {
                return closeTypePromise;
            },
            close: () => {
                const closeEvent = new Event('close');
                element.dispatchEvent(closeEvent);
            },
            currentTarget: element,
        };
    }
    async showToast(text, attributes = {}) {
        if (typeof self.document === 'undefined') {
            throw new Error('AUPSDK.showToast() is only available in the browser');
        }
        const { showToast } = this.getConfig();
        if (!showToast) {
            throw new Error('Config.showToast is not defined');
        }
        let resolveDataPromise;
        let resolveCloseTypePromise;
        const dataPromise = new Promise((resolve) => {
            resolveDataPromise = resolve;
        });
        const closeTypePromise = new Promise((resolve) => {
            resolveCloseTypePromise = resolve;
        });
        const onClose = (options) => {
            const closeType = (options === null || options === void 0 ? void 0 : options.type) || 'close';
            resolveCloseTypePromise(closeType);
            resolveDataPromise({});
        };
        await showToast(text, attributes, onClose);
        return {
            async data() {
                return dataPromise;
            },
            async closeType() {
                return closeTypePromise;
            },
            close: () => {
                onClose({ type: 'close' });
            },
            // Temporary solution until HTML element support is implemented
            // Setting to null since we don't have a DOM element reference
            currentTarget: null,
        };
    }
    /**
     * Define custom collaboration SDK UI components on {@link Config.customElements}
     */
    async loadUIComponent(tagName) {
        const moduleNames = Object.keys(MODULE_CONFIGS);
        for (const moduleName of moduleNames) {
            if (isUIModule(moduleName) &&
                MODULE_CONFIGS[moduleName].uiComponents.includes(tagName)) {
                const module = await this._load(moduleName);
                /**
                 * Typescript infers an intersection of all possible loadUIComponent(tagName: 'my-element').
                 * For an intersection of many `...tagName` (spreads), the intersection of all args explodes and
                 * resolves to `never`.
                 *
                 * This is an intentional limitation of TypeScript to avoid combinatorial explosion when
                 * exhasuting the possible combinations of function arguments.
                 * @see https://twitter.com/SeaRyanC/status/1544414378383925250
                 *
                 * The combinatory explosion is caused by serveral components that accept many tag names
                 * to parallelize the load: `module.loadUIComponent('cc-asset-organizer', 'cc-assignment-thumbnail', ...)`.
                 *
                 * @TODO The SDK can manage parallelization here. Components can provide single tag at a time without the spread,
                 *       reducing the combinatorial complexity, overall setup complexity, and use of `never`.
                 */
                return await module.loadUIComponent(tagName);
            }
        }
        throw new TypeError(`Unknown tag name supplied: AUPSDK.loadUIComponent('${tagName}')`);
    }
    async registerMobxCreateAtom(target) {
        const { sam } = await this._load('SAM');
        return sam.makeObservable(target);
    }
    /**
     * @deprecated Use {@link AssetContext.getAssetLinkManager}
     */
    async getAssetLinkManager() {
        const assetContext = await this.getAssetContext();
        return assetContext.getAssetLinkManager();
    }
    async getAssetContext() {
        const { sam } = await this._load('SAM');
        return sam.getAssetContext();
    }
    async getBoardContext() {
        const { sam } = await this._load('SAM');
        return sam.getBoardContext();
    }
    async getClassroomContext() {
        const { sam } = await this._load('SAM');
        return sam.getClassroomContext();
    }
    async getCustomModelContext() {
        const { sam } = await this._load('SAM');
        return sam.getCustomModelContext();
    }
    async getUAMContext() {
        const uamContext = await this._load('AccountMenuMiniApp');
        return uamContext;
    }
    async getUAMTriggerContext() {
        const uamTriggerContext = await this._load('AccountMenuTrigger');
        return uamTriggerContext;
    }
    /**
     * @deprecated
     * Returns the deprecated CCLibraries JS API instance that may be shared with the CCLibraries Panel.
     * (The panel only loads when you call `loadUIComponent('cc-libraries')` with the relevant customElement tagName.)
     */
    async getDeprecatedLibraryContext() {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const { ccLibraries } = await this.getPrivateLibraryContext();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return ccLibraries;
    }
    /**
     * @internal
     * Gets the CCLibraries Panel module.
     */
    async getPrivateLibraryContext() {
        return await this._load('CCLibraries');
    }
    /**
     * Returns SAM's LibraryContext.
     */
    async getLibraryContext() {
        const { sam } = await this._load('SAM');
        return sam.getLibraryContext();
    }
    async getProjectContext() {
        const { sam } = await this._load('SAM');
        return sam.getProjectContext();
    }
    async getCollaborationContext() {
        const { sam } = await this._load('SAM');
        return sam.getCollaborationContext();
    }
    async getGenerationHistoryContext() {
        const { sam } = await this._load('SAM');
        return sam.getGenerationHistoryContext();
    }
    async getWorkfrontContext() {
        const { sam } = await this._load('SAM');
        return sam.getWorkfrontContext();
    }
    async getOrchestratorContext() {
        const { orchestratorContext } = await this._load('OrchestratorContext');
        return orchestratorContext;
    }
    /**
     * Creates a shared context instance that is used to share data between multiple modules.
     * This can be used by clients of the SDK or internally by modules loaded via the SDK.
     * @example
     * ```ts
     *   // Load the web components which will share context
     *   await sdk.loadUIComponent('cc-component-a');
     *   await sdk.loadUIComponent('cc-component-b');
     *
     *   // Using the new SDK method create a new shared context object
     *   const sharedContext = sdk.createSharedContext<SharedContextData>({type:'module-x', a: 1, b:2});
     *
     *   // Render various web components and provide them with the context ID guid via the contextReferenceId attribute
     *   const componentA = () => <cc-component-a contextReferenceId={sharedContext.referenceId}/>;
     *   const componentB = () => <cc-component-b contextReferenceId={sharedContext.referenceId}/>;
     *
     *   // Update shared context object
     *   sharedContext.updateContext({a:3, b:4});
     *
     *   // Provide shared context ID to SDK method
     *   sdk.performSomeAction(sharedContext.referenceId);
     * ```
     */
    createSharedContext(data) {
        const sharedContext = new SharedContext(data);
        this._sharedContextMap.set(sharedContext.referenceId, sharedContext);
        return Promise.resolve(sharedContext);
    }
    /**
     * Retrieves a shared context instance by reference ID
     * This can be used by clients of the SDK or internally by modules loaded via the SDK.
     * @example
     * ```ts
     *   class WebComponent extends HTMLElement {
     *      _updateSharedContext() {
     *          const contextReferenceId = this.getAttribute('contextReferenceId');
     *
     *          if (contextReferenceId) {
     *              // Get the shared context object for the given contextReferenceId value
     *              const sharedContext = this.sdk.getSharedContext(contextReferenceId);
     *
     *              // Access shared context and use as required
     *              this._sharedContext = sharedContext.getContext();
     *          }
     *      }
     *
     *      connectedCallback() {
     *          const contextReferenceId = this.getAttribute('contextReferenceId');
     *
     *          if (contextId) {
     *              const sharedContext = this.sdk.getSharedContext(contextReferenceId);
     *
     *              // Attach change listener to be notified of future updates from the host
     *              sharedContext.on('change', this._updateSharedContext);
     *
     *              // Initialize state after attaching or changing contextReferenceId value
     *              this._updateSharedContext();
     *          }
     *      }
     *  }
     * ```
     */
    getSharedContext(contextReferenceId) {
        return Promise.resolve(this._sharedContextMap.get(contextReferenceId));
    }
    /**
     * Creates a shared context that is used to store and pass around contextual information
     * that will be reported in the analytics event.
     *
     * To report an event as an implementation module, you must create a workflow context
     * containing the component name and version, as this information will be used to
     * identify which implementation module is reporting the event.
     *
     * @example
     * ```ts
     *   // Define a workflowContext object
     *   // The `component` section is mandatory, as it's used to identify which implementation module is reporting the event
     *   const workflowData: Analytics.WorkflowContext = {
     *       component: {
     *           name: 'ShareSheet',
     *           version: '1.2.3',
     *       }
     *       asset: {
     *           id: '123',
     *           name: 'my asset'
     *       },
     *   };
     *
     *   // Create a workflow context to store event-related information
     *   const workflowContext = await sdk.createWorkflowContext(workflowData);
     * ```
     */
    async createWorkflowContext(data) {
        const analytics = await this._getAnalyticsContext();
        return analytics.createWorkflowContext(data);
    }
    /**
     * Fires a pre-defined analytics event.
     *
     * After creating a workflow context, fire a pre-defined event with fireAnalyticsEvent. Pass in
     * an event name, the workflow context ID, and optionally, a payload for any information
     * not available when making the workflow context such as error information or
     * information about newly-created assets.
     *
     * @example
     * ```ts
     *   deleteShareSheetArtifact()
     *       .then((result) => {
     *           sdk.fireAnalyticsEvent('artifact.delete.complete', workflowContext.referenceId)
     *       })
     *       .catch((err) => {
     *           sdk.fireAnalyticsEvent('artifact.delete.error', workflowContext.referenceId, { error: { code: err }})
     *       })
     * ```
     */
    async fireAnalyticsEvent(event, workflowContextId, payload) {
        const analytics = await this._getAnalyticsContext();
        return analytics.fireEvent(event, workflowContextId, payload);
    }
    /**
     * For host applications only -- fires an event not defined in the analytics SDK's workflow files.
     * Host applications must specify whether the event is operational (i.e. without PII),
     * behavioural (i.e. with PII), or both. If both is specified, then two events will be sent,
     * one with PII and one without. If no value is specified, the event class defaults to 'behavioral'.
     * Events fired with fireGenericAnalyticsEvent are automatically reported to the host application's ingest instance.
     *
     * @example
     * ```ts
     *   const event: Analytics.GenericEventPayload = {
     *       event: {
     *           type: 'click',
     *           subtype: 'create',
     *           subcategory: 'templates',
     *           workflow: 'asset-creation',
     *       },
     *       ui: {
     *           viewType: 'grid',
     *           searchKeyword: 'minimalist',
     *       }
     *   };
     *
     *   sdk.fireGenericAnalyticsEvent(event);
     * ```
     */
    async fireGenericAnalyticsEvent(payload, eventClass = ['behavioral']) {
        const analytics = await this._getAnalyticsContext();
        await analytics.fireGenericEvent(payload, eventClass);
    }
    /**
     * Stops and cleans up analytics functionality. If given a module ID, then stops analytics
     * only for the one module. Otherwise, stops analytics for all modules.
     */
    async stopAnalytics(appOrModule) {
        const analytics = await this._getAnalyticsContext();
        return analytics.stopAnalytics(appOrModule);
    }
    /**
     * Flushes the analytics queue and sends all queued events immediately. If given a component
     * ID, it flushes only that component's queue. Otherwise, it flushes all existing queues.
     */
    async flushAnalyticsQueue(appOrModule) {
        const analytics = await this._getAnalyticsContext();
        return analytics.flushQueue(appOrModule);
    }
    /**
     * @internal
     */
    async _getAnalyticsContext() {
        return await this._load('Analytics');
    }
}
/**
 * Initialize an AUPSDK instance
 */
export function preloadSDK(namedRelease, initialConfigOptions) {
    _rootSDK = _rootSDK || new AUPSDK(namedRelease, initialConfigOptions);
    return _rootSDK;
}
/**
 * Get an AUPSDK instance
 */
export function getSDK() {
    if (!_rootSDK) {
        throw new Error('SDK not initialized. Call preloadSDK first.');
    }
    return _rootSDK;
}
/**
 * Check if a module is a UI module
 */
export function isUIModule(name) {
    return 'uiComponents' in MODULE_CONFIGS[name];
}
